"use client";

// LandingOrbit — the dashboard's OrbitView, carried onto the landing as a
// brief but ACCURATE depiction. Same geometry and same feel as the app:
// multi-ring galaxy around netti's origin, newest work seated at the crown's
// apex with age fanning out both ways, a gentle spiral twist per ring,
// deterministic jitter, per-tile lag so the field trails the creature — and
// the same "netti turns first" chain (target → netti's own spin spring → the
// field following a beat later → each tile trailing the field).
//
// What the landing takes OUT: wheel/drag, pages, tile drag, context menus.
// What it puts IN: a slow, graceful AUTO-REVOLVE — the depiction turns on its
// own, forever, and netti co-rotates through the organism drive bus exactly
// the way OsShell hands it the revolve (rotation = spin * 0.6, eased by the
// creature's own spring).
//
// The origin can sit at the TOP of the stage (the hero — netti crowns the
// screen, the field fans down out of it) or at the BOTTOM (the create act —
// the dashboard's own seat). Both run the same math: bottom-origin space is
// the canonical one, and a top origin simply mirrors y.

import { useEffect, useMemo, useRef, useState } from "react";
import AppIcon from "../../brand/AppIcon";
import CellThreads from "../../brand/CellThreads";
import { arcPoints, seedOf, type Thread } from "../../../lib/brand/thread";
import DirIcon from "../../os/DirIcon";

export interface OrbitItem {
  key: string;
  kind: "app" | "folder" | "page" | "cell";
  /** BrandIcon name for kind "app". */
  app?: string;
  /** the label under a folder / page tile (apps stay label-less in the hero). */
  name?: string;
  /** emoji stamped on the folder/page face — relevant to the title. */
  symbol?: string;
  /** the quiet date line (the create act shows it; the hero doesn't). */
  meta?: string;
}

interface Placed {
  it: OrbitItem;
  baseAngle: number;
  radius: number;
  ring: number;
  lag: number;
  /** this ring's share of the revolve — the crown turns a touch faster than the
   *  ring inside it (grid fields only; 1 everywhere else). */
  spin: number;
  i: number; // entrance order (seating order — newest first)
  /** decorative bare-cell width in px (kind "cell" only). */
  cs?: number;
}

const TAU = Math.PI * 2;
const UP = Math.PI / 2;

// the dashboard's own chain — netti leads, the field follows (OrbitView).
const NETTI_FACTOR = 0.6;
const NETTI_K = 14;
const NETTI_C = 2 * 0.9 * Math.sqrt(NETTI_K);
const FOLLOW = 0.22;
const TILE_LAG = 0.42;

const easeOut = (t: number) => 1 - Math.pow(1 - Math.min(1, Math.max(0, t)), 3);
/** half a tile's box on a phone (.t1-orbit2 .orbit-tile is 104px wide there) —
 *  what a spread field measures its edge fade against. */
const TILE_HALF_W = 52;

/* ── THE PHONE'S CIRCULAR GRID (`phoneGrid`) ───────────────────────────────
   A revolving field on a 375px screen cannot be tuned by feel: every knob
   (crown angle, reach, per-ring weights) trades against every other, and the
   result was a huddle in which labels sat on top of neighbouring folders and a
   whole ring carried two seats. So the phone field is laid on a GRID instead —
   invisible concentric circles with two hard laws:

     ALONG a ring   neighbours are exactly `GRID_PITCH` of ARC apart, so the
                    spacing an eye reads is the same on every ring.
     ACROSS rings   consecutive radii differ by `GRID_BAND`, which is the
                    DIAGONAL of a tile's ink. Two boxes whose centres are at
                    least their own diagonal apart cannot overlap at ANY angle
                    — which is what makes the anti-collision hold while the
                    rings turn at DIFFERENT speeds and every relative phase
                    eventually happens.

   Everything else follows from those two: the seat count of a ring is however
   many whole pitches fit its circumference (so no ring ever turns an empty
   stretch to the viewer), and the number of rings is however many whole bands
   fit between the core and the far edge. Change a tile's size and the band and
   pitch below must change with it — they are measurements of the tile, not
   taste. See the matching `.t1-orbit2--grid` block in test1.css. */
const GRID_TILE_W = 84;   // .t1-orbit2--grid .orbit-tile box width
const GRID_TILE_H = 68;   // icon + ONE label line + the tile's own padding
const GRID_INK_W = 74;    // the widest ink inside that box (a full-width label)
/** the radial band — a tile's diagonal, so no two tiles can ever meet. */
const GRID_BAND = Math.round(Math.hypot(GRID_INK_W, GRID_TILE_H)); // 101
/** the arc between neighbours on a ring (the box + real air). */
const GRID_PITCH = 106;
/** the icon box on the grid — folders at this width, apps at /1.36 of it. */
const GRID_DIR_W = 56;
/** how much faster the crown turns than the middle ring (and the inner one,
 *  slower by the same step) — John's "the outer grid could be a touch quicker". */
const GRID_RING_SPEED = 0.14;

function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) h = (h ^ s.charCodeAt(i)) * 16777619;
  return ((h >>> 0) % 1000) / 1000;
}

/** CURATED RANDOM — the field must LOOK unplanned and BE even.
 *  Pure hashing (what this used) clumps: three folders land together, a whole
 *  arc goes app-less, two deco cells sit on top of each other. So both layers
 *  are STRATIFIED instead — the randomness lives inside a cell of a lattice,
 *  never across the whole field.
 *
 *  This one interleaves the items by KIND, so every stretch of every ring
 *  carries a mix (app · folder · page) rather than a run of one. Order within a
 *  kind is preserved, so "newest first" still holds inside each species. */
function interleaveKinds(items: OrbitItem[]): OrbitItem[] {
  const buckets = new Map<string, OrbitItem[]>();
  for (const it of items) {
    const k = it.kind;
    if (!buckets.has(k)) buckets.set(k, []);
    buckets.get(k)!.push(it);
  }
  const lanes = [...buckets.values()];
  if (lanes.length < 2) return items;
  // deal round-robin, weighted by how many each lane holds, so a lane with
  // twice the items appears twice as often instead of running out early
  const out: OrbitItem[] = [];
  const cursor = lanes.map(() => 0);
  const total = items.length;
  while (out.length < total) {
    // whichever lane is furthest "behind" its share goes next
    let best = -1;
    let bestDebt = -Infinity;
    for (let i = 0; i < lanes.length; i++) {
      if (cursor[i] >= lanes[i].length) continue;
      const debt = (out.length + 1) * (lanes[i].length / total) - cursor[i];
      if (debt > bestDebt) { bestDebt = debt; best = i; }
    }
    if (best < 0) break;
    out.push(lanes[best][cursor[best]++]);
  }
  return out;
}

/** OrbitView's seat order — item 0 at `centre`, the rest fanning to both sides. */
function centreOut(m: number, centre: number, wrap: boolean): number[] {
  const order: number[] = [];
  const seen = new Set<number>();
  const put = (i: number) => {
    if (!wrap && (i < 0 || i > m - 1)) return;
    const k = wrap ? ((Math.round(i) % m) + m) % m : Math.round(i);
    if (seen.has(k)) return;
    seen.add(k);
    order.push(k);
  };
  const c = Math.round(centre);
  put(wrap ? c : Math.max(0, Math.min(m - 1, c)));
  for (let d = 1; order.length < m && d <= m; d++) {
    put(c - d);
    put(c + d);
  }
  return order;
}

export default function LandingOrbit({
  items,
  origin = "bottom",
  /** how far above (top) / below (bottom) the stage edge the core sits, as a
   *  fraction of the stage's smaller side. */
  sink = 0.08,
  /** clear air between the crown ring and the stage's far edge (px) — raise it
   *  when words live just past that edge (the hero's headline). */
  farPad,
  /** radians per second of auto-revolve — slow and graceful. */
  rate = 0.05,
  /** run the loop + drive netti's rotation only while the depiction owns the
   *  screen; an inactive orbit is frozen and silent. */
  active = true,
  /** co-rotate the creature through the drive bus (one orbit at a time). */
  driveNetti = true,
  /** ms before the entrance begins once `active` lands. */
  enterDelay = 0,
  threads: withThreads = false,
  /** show each tile's date line (the create act does; the hero doesn't). */
  showMeta = false,
  /** wrap the full circle and revolve endlessly (default) — or hold a still
   *  fan inside the visible window. */
  revolve = true,
  /** scale every tile (the create act runs at 1.25). */
  iconScale = 1,
  /** render folders at EXACTLY the size the projects view renders them, rather
   *  than at the orbit's own optical volume. See `dirSize` below. */
  matchProjectsView = false,
  /** on a PHONE, spread the field: a narrower crown (which buys a much bigger
   *  radius), three rings instead of two, and a slightly quicker turn so every
   *  item comes round. For a field that owns its own pane. */
  spreadOnPhone = false,
  /** the spread field's outer ring, as a fraction of the stage WIDTH. 0.7 is
   *  the create act's full reach; the hero passes 0.55 because it shares the
   *  screen with its own headline — a smaller outer pulls every ring in with
   *  it (the inner ring is 0.4 of the outer). */
  spreadReach = 0.7,
  /** lay the phone's spread field on the CIRCULAR GRID — even arc pitch along
   *  every ring, a radial band wide enough that two tiles can never meet, and
   *  rings that turn at their own speeds (the crown leads). See the GRID_*
   *  block above. Implies `spreadOnPhone` + `fullCircle`; ignored on desktop. */
  phoneGrid = false,
  /** EVERY RING IS A FULL CIRCLE, AND EVERY ITEM SITS AT EXACTLY ONE SEAT.
   *  The pool is laid once around the whole field (outer rings carrying more,
   *  a spacing floor per ring), so the revolve never turns an empty half to
   *  the viewer AND nothing repeats within the circle — an icon comes round
   *  again only when its own seat does. Desktop hero keeps its curated
   *  `ringCounts` (that branch wins when both are given). */
  fullCircle = false,
  /** let the crest of the arc read whole: the stage stops clipping and the far
   *  edge only dims instead of dissolving. For a field that lives INSIDE a pane
   *  (the create act), where the top of the arc is a section boundary rather
   *  than the top of the window. The hero must not use this — its crest is the
   *  window edge, and a tile there would be cut by the browser instead. */
  keepCrest = false,
  /** decorative bare cells scattered between the rings — the mock's little
   *  white blobs that make the field read alive rather than charted. */
  deco = 0,
  /** force a ring count (the hero asks for exactly two circles). Default: the
   *  band decides for itself, as the dashboard's orbit does. */
  rings,
  /** exact seats per ring, OUTER FIRST (e.g. [9, 6]) — a curated field rather
   *  than a proportional share. Extra items are dropped; missing ones simply
   *  leave a ring lighter. Ignored on a phone, which keeps its own tight cap. */
  ringCounts,
  className,
}: {
  items: OrbitItem[];
  origin?: "top" | "bottom";
  sink?: number;
  farPad?: number;
  rate?: number;
  active?: boolean;
  driveNetti?: boolean;
  enterDelay?: number;
  /** THE CELL THREADS along each ring (the evolved homepage's wheel,
   *  components/landing/home/HomeWheel): the dotted orbit lines the tiles ride,
   *  drawn once per ring and faded in as the tiles arrive. */
  threads?: boolean;
  showMeta?: boolean;
  revolve?: boolean;
  iconScale?: number;
  matchProjectsView?: boolean;
  spreadOnPhone?: boolean;
  spreadReach?: number;
  phoneGrid?: boolean;
  fullCircle?: boolean;
  keepCrest?: boolean;
  deco?: number;
  rings?: number;
  ringCounts?: number[];
  className?: string;
}) {
  const stageRef = useRef<HTMLDivElement | null>(null);
  const tileEls = useRef<Map<string, HTMLElement>>(new Map());
  const [size, setSize] = useState({ w: 0, h: 0 });
  const [isNarrow, setIsNarrow] = useState(false);
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    setReduced(window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false);
    const mq = window.matchMedia("(max-width: 640px)");
    const on = () => setIsNarrow(mq.matches);
    on();
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (w > 0 && h > 0) setSize((d) => (d.w === w && d.h === h ? d : { w, h }));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // ── geometry + seating — OrbitView's placement, one still page ───────────
  const geom = useMemo(() => {
    const b = Math.min(size.w, size.h);
    const cx = size.w / 2;
    // canonical space: origin just under the bottom edge; a top origin mirrors y.
    const cy = size.h + b * sink;
    const pad = farPad ?? (isNarrow ? 66 : 96);
    // a phone's inner ring starts further OUT — the field should sit near the
    // bloom's outer petals, not huddle at the core (John: "near the outer
    // edges of the netti so it feels organic")
    const minInner = b * (isNarrow ? 0.46 : 0.38);
    // ── THE RADIUS IS DERIVED FROM WHAT FITS, not guessed ──────────────────
    // A seat at the edge of the visible window sits at sin(arc/2) × r from the
    // centre line. If that exceeds (half the stage − half a tile) the tile is
    // clipped by the stage's overflow — which is exactly how "welcome copy"
    // became "ome copy" on a phone. So the outer ring is bounded by the x that
    // keeps the OUTERMOST seat whole, as well as by the far edge.
    // ⚠️ these two must MATCH the seating's windowArc / the tile's real width
    // (104px on a phone → 52 + a margin), or the fit bound is a fiction
    const halfArc = (isNarrow ? Math.PI * 0.66 : Math.PI * 1.04) / 2;
    // on a phone the crown fade (see paint) hides everything past ~65° off
    // vertical, so the fit only has to hold the seats INSIDE that crown — a
    // narrower angle than the seating window, which lets the radius breathe.
    // The tile is 104px + its label — 62 + an 8px inset keeps the last seat whole.
    // ⚠️ THE CROWN ANGLE IS WHAT LIMITS THE RADIUS. A seat sits sin(θ)×r from the
    // centre line, so a wide crown (±72°) forces r down to whatever keeps the
    // outermost seat on screen — about 131px on a 390px phone, which is why the
    // field read as a small huddle at the bottom rather than an arc. Pulling the
    // crown in to ±36° nearly doubles the radius for the same screen width: the
    // field becomes a dome that rises out of the bloom instead of a puddle.
    const spread = isNarrow && spreadOnPhone;
    const grid = spread && phoneGrid;
    // ±21.6° for a spread field: r is capped at (half the stage − half a tile)
    // ÷ sin(crown), so every degree taken off the crown buys radius. At ±36° the
    // arc topped out ~220px above the bottom; at ±21.6° it reaches ~360px — a
    // dome across the lower half of a phone rather than a puddle in the corner.
    // ⚠️ on a spread phone field the crown does NOT bound the radius at all.
    // That field dissolves tiles by their distance from the bezel (see paint),
    // so it is allowed to reach past the sides — the radius is then set by
    // height alone (yCap), and the arc stretches edge to edge as John asked.
    const crownHalf = isNarrow ? Math.PI * (spread ? 0.5 : 0.4) : halfArc;
    const halfTile = isNarrow ? (spread ? 56 : 62) : 82;
    const xFit = spread
      ? Infinity
      : Math.max(90, (cx - halfTile - (isNarrow ? 8 : 6)) / Math.max(0.2, Math.sin(crownHalf)));
    // a phone caps the crown at ~92% of the stage's height — the stage box
    // itself already stops above the words (the hero's .t1-hero__orbit ends
    // 40svh up), so the field may reach nearly to the box's own edge
    // the spread field's reach. ⚠️ HEIGHT ALONE CHOSE A CIRCLE THE WIDTH COULD
    // NOT SHOW: at 0.86×stage the outer ring hit r≈500 on a ~390px screen, and
    // the arc inside the viewport was a sliver — 3 tiles visible from a 23-item
    // circle, however the seats were weighted. A ring only shows a real arc
    // when its radius is comparable to the screen's width, so the outer ring is
    // capped at 0.7×width (~273 on a phone): the crown still rises ~a third of
    // the screen, and every ring keeps enough of its circle on stage for the
    // 1.5 · 3.5 · 3 the field is tuned to show.
    const yCap = isNarrow ? size.h * (spread ? 0.86 : 0.92) : Infinity;
    const wCap = spread ? size.w * spreadReach : Infinity;
    // ── THE GRID'S OWN FAR BOUND ──────────────────────────────────────────
    // Every other cap here is a fraction of something and lands where it lands.
    // The grid's outer ring has an exact job: its CREST must clear the far-edge
    // dissolve, or the crown of the field is a row of half-faded tiles. The
    // crest sits `cy - r` from the far edge (true for a mirrored top origin
    // too — the mirror flips y and the measurement with it), the dissolve owns
    // the last `fadeD` of the stage, and the tile's own half-height has to
    // clear it as well. Solve that for r and you get the bound, whatever the
    // phone. ⚠️ keep `fadeD` in step with paint()'s.
    const fadeD = Math.max(110, pad + 30);
    const gridCap = grid ? cy - fadeD - GRID_TILE_H / 2 : Infinity;
    // ⚠️ THE FLOOR WAS BEATING THE FIT. `max(minInner + 70, min(…, xFit))` let
    // a large phone minInner (b×0.46 ≈ 172px) push outerR to 242 while xFit
    // said 122 — the fit bound was computed and then discarded, which is why
    // three rounds of tightening it changed nothing. On a phone the FIT wins:
    // outerR is what fits, and the inner ring is derived a band inside it.
    const fitR = Math.min(cy - pad, xFit, grid ? gridCap : yCap, wCap);
    const outerR = isNarrow ? Math.max(120, fitR) : Math.max(minInner + 70, fitR);
    // ── HOW MANY RINGS, AND WHERE ─────────────────────────────────────────
    // Not a number anyone picked: as many WHOLE bands as fit between the outer
    // ring and a floor that keeps the innermost circle wide enough to still
    // read as an arc across the screen (a small circle near the core is a
    // huddle, not an orbit). A short phone therefore gets two rings and a tall
    // one gets three, and neither can ever collide.
    const gridRings = grid
      ? Math.max(1, Math.min(3, Math.floor((outerR - Math.max(112, outerR * 0.32)) / GRID_BAND) + 1))
      : 0;
    // the inner ring on a phone: a clear band inside the crown, never the core
    // a spread field starts its inner ring proportionally, so the three rings sit
    // as an evenly-breathing band rather than one ring hugging the core.
    const minInnerFit = grid
      ? outerR - (gridRings - 1) * GRID_BAND
      : isNarrow
        ? spread
          ? Math.max(64, outerR * 0.4)
          : Math.max(64, Math.min(minInner, outerR - 92))
        : minInner;
    return { b, cx, cy, minInner: minInnerFit, outerR, pad, spread, grid, gridRings, crownHalf };
  }, [size, isNarrow, sink, farPad, spreadOnPhone, spreadReach, phoneGrid]);

  const tiles = useMemo<Placed[]>(() => {
    if (!items.length || !size.w) return [];
    const { minInner, outerR, spread, grid, gridRings } = geom;
    // a WIDER crown on a desktop (a fuller sweep of the circle is visible), a
    // TIGHTER one on a phone (fewer seats, higher, never near the words)
    // ⚠️ the seating window must not exceed the crown the FIT was computed for,
    // or seats are placed where a tile cannot be whole on screen.
    const windowArc = isNarrow ? Math.PI * (spread ? 1.0 : 0.8) : Math.PI * 1.04;
    // a phone packs its seats closer along the arc (the tiles are 104px; a
    // spread field's are smaller again, so they sit closer still)
    const tileArc = isNarrow ? (spread ? 84 : 100) : 172;
    // a deeper band on a phone spreads the rings apart, so the field occupies
    // the bloom instead of a thin arc across its middle. A spread field packs
    // its rings tighter so THREE fit inside the same reach — dense, but with
    // real air between the seats because the radius grew with them.
    const tileBand = isNarrow ? (spread ? 68 : 118) : 148;
    const seq = interleaveKinds(items);
    const n = seq.length;

    const bandRings = Math.max(1, Math.round((outerR - minInner) / tileBand) + 1);
    // a spread phone field is THREE rings, always — the whole point of the reach
    // is that the band is deep enough to read as a field rather than an arc.
    // (`rings` is a desktop instruction: on a spread phone the depth IS the
    // design, so three wins even when a caller fixes the desktop at two.)
    const ringCount = grid
      ? gridRings
      : spread ? 3 : rings ?? Math.max(1, Math.min(bandRings, Math.round(Math.sqrt(Math.max(1, n)) * 0.9) + 1));
    /** the crown turns fastest, the innermost ring slowest — one step of
     *  GRID_RING_SPEED either side of the middle. Off the grid every ring
     *  shares the one revolve, exactly as before. */
    const spinOf = (k: number) =>
      grid && ringCount > 1 ? 1 + (k - (ringCount - 1) / 2) * GRID_RING_SPEED : 1;
    const ringR = (k: number) =>
      ringCount <= 1 ? Math.min(outerR, (minInner + outerR) / 2) : minInner + (outerR - minInner) * (k / (ringCount - 1));
    const radii = Array.from({ length: ringCount }, (_, k) => ringR(k));
    const capFull = (r: number) => Math.max(2, Math.floor((TAU * r) / tileArc));
    const capWin = (r: number) => Math.max(1, Math.floor((windowArc * r) / tileArc));

    // ring counts — outer-first for a revolving field (the crown carries the
    // recent work), proportional-to-window for a still fan.
    let counts: number[];
    if (revolve && ringCounts && !isNarrow) {
      // CURATED: `ringCounts` is how many the eye should see IN THE WINDOW per
      // ring, outer first — the hero's 9 · 6. A revolving ring wraps the whole
      // circle, so the seats around it are that count scaled by the window's
      // share of the circle (a half-circle window at 9 visible = ~18 seats),
      // and the field is drawn from the item pool as far as it goes.
      const winShare = windowArc / TAU;
      counts = new Array(ringCount).fill(0);
      let rem = n;
      for (let ring = ringCount - 1; ring >= 0 && rem > 0; ring--) {
        const vis = ringCounts[ringCount - 1 - ring] ?? 0;
        const want = Math.round(vis / winShare);
        counts[ring] = Math.min(capFull(radii[ring]), want, rem);
        rem -= counts[ring];
      }
    } else if (revolve && fullCircle) {
      // ── THE UNIQUE CIRCLE ─────────────────────────────────────────────────
      // ⚠️ Two failure modes taught this shape. A ring seated as a FAN revolves
      // its whole population out of view and leaves half a turn empty (the old
      // emptying field); a ring FILLED to its circumference from a small pool
      // repeats the pool — the same folder three times in one glance, which is
      // worse. So the pool is laid around the field exactly ONCE: every ring is
      // a full circle (evenly seated, so the revolve never empties), every item
      // appears at exactly one seat, and an icon "repeats" only the way a
      // carousel repeats — when its own seat comes round again.
      //
      // Distribution: outer rings carry more (weight r^1.4), and each ring has
      // a FLOOR on the arc between neighbours — widest at the inner ring,
      // tightening a little as the rings go out — so the field reads spaced,
      // not packed. If the pool outruns the pitch caps, the overflow simply
      // goes unseated: spacing is the design; the pool is only the supply.
      // ⚠️ A PHONE HIDES MOST OF THE CIRCLE, and it hides each ring by a
      // different amount: the inner ring shows half its circle, the middle
      // ~44%, the outer barely a quarter (it runs past the bezel). Weighting by
      // radius alone seated the outer ring heaviest — exactly where seats are
      // least visible — and the field read near-empty (3 visible of 20). The
      // spread weights are chosen for what the WINDOW shows: ~1.5 on the inner
      // ring, ~3.5 in the middle, ~3 on the crown, John's numbers. The pitch
      // floors keep the arc widest inside, tightening a little outward.
      // Each ring's seat count comes from a TARGET PITCH — the arc between
      // neighbours — widest on the inner ring and tightening a little outward
      // (John's rule). On a spread phone the pitches are absolute [2.6 · 1.35 ·
      // 1.2]×tileArc, which lands the window on ~1.5 inner · ~3.5 middle · ~3
      // crown; a desktop keeps its gentler ramp. When the pool cannot fill
      // every seat, all rings thin TOGETHER (largest-remainder scale), so the
      // shape survives a short pool; when the pool outruns the seats, the
      // overflow goes unseated — spacing is the design, the pool is supply.
      // ON THE GRID the pitch is ONE number for every ring — that is the whole
      // point of a grid. The weighted ladder below it is the pre-grid spread
      // field, kept for callers that have not moved over.
      const pitchFor = (k: number) =>
        grid
          ? GRID_PITCH
          : tileArc *
            (spread
              ? [2.6, 1.35, 1.2][Math.min(k, 2)]
              : ringCount <= 1
                ? 1.8
                : 2.2 - 0.6 * (k / (ringCount - 1)));
      const caps = radii.map((r, k) => Math.max(1, Math.floor((TAU * r) / pitchFor(k))));
      const capSum = caps.reduce((a, b) => a + b, 0);
      // a grid seats every ring to its own capacity — the pitch already decided
      // the spacing, so a "share" would only reintroduce the empty sweeps.
      const weights = spread || grid ? caps.map((c) => c) : radii.map((r) => Math.pow(r, 1.4));
      const wSum = weights.reduce((a, b) => a + b, 0) || 1;
      const budget = Math.min(n, capSum);
      const ideal = weights.map((w) => (budget * w) / wSum);
      counts = ideal.map((v, k) => Math.min(caps[k], Math.floor(v)));
      let leftover = budget - counts.reduce((a, b) => a + b, 0);
      const order = ideal
        .map((v, k) => ({ k, frac: v - Math.floor(v) }))
        .sort((a, b) => b.frac - a.frac);
      for (let oi = 0; leftover > 0 && oi < 64; oi++) {
        const { k } = order[oi % order.length];
        if (counts[k] < caps[k]) { counts[k]++; leftover--; }
      }
    } else if (revolve) {
      // the field ALWAYS spreads across the ring band (two arcs, three on a
      // band deep enough) — one wide ring reads as a chart, not a galaxy, and
      // strands most of a phone's tiles off the visible wedge.
      counts = new Array(ringCount).fill(0);
      let rem = n;
      // the OUTER ring (index ringCount-1, filled first) carries the larger
      // share; the ring inside it carries the rest — a fuller crown over a
      // quieter inner orbit, not two equal circles.
      const shares = isNarrow ? [0.58, 1] : ringCount >= 3 ? [0.44, 0.34, 1] : [0.62, 1];
      for (let ring = ringCount - 1; ring >= 0 && rem > 0; ring--) {
        const si = ringCount - 1 - ring;
        const share = si < shares.length - 1 && ringCount > 1 ? Math.ceil(n * shares[si]) : Infinity;
        counts[ring] = Math.min(capFull(radii[ring]), share, rem);
        rem -= counts[ring];
      }
    } else {
      const caps = radii.map((r) => capWin(r));
      const W = caps.reduce((s, w) => s + w, 0) || 1;
      counts = caps.map((w) => Math.max(0, Math.round((n * w) / W)));
      let diff = n - counts.reduce((s, c) => s + c, 0);
      while (diff > 0) {
        let best = 0;
        for (let k = 1; k < ringCount; k++) if (caps[k] - counts[k] > caps[best] - counts[best]) best = k;
        counts[best]++;
        diff--;
      }
      while (diff < 0) {
        let best = 0;
        for (let k = 1; k < ringCount; k++) if (counts[k] > counts[best]) best = k;
        if (counts[best] <= 0) break;
        counts[best]--;
        diff++;
      }
    }

    const out: Placed[] = [];
    let i = 0;
    // Σcounts ≤ n in every mode now — the pool is never repeated. (The unique
    // circle above may seat FEWER than n; the leftovers simply don't appear.)
    const total = Math.min(n, counts.reduce((a, c) => a + c, 0));
    for (let ring = ringCount - 1; ring >= 0 && i < total; ring--) {
      const r = radii[ring];
      const m = Math.min(counts[ring], total - i);
      if (m <= 0) continue;
      // the spiral twist — radial lines become arms. With only TWO circles the
      // twist alone still lets seats line up radially, so each ring also takes a
      // deliberate HALF-SEAT stagger: the two paths read as two orbits, never
      // as one grid drawn twice.
      const phase = ring * 0.62 + (ring % 2 ? Math.PI / Math.max(4, m) : 0);
      const lag = TILE_LAG - Math.min(0.16, ring * 0.04);
      const wraps = revolve && (fullCircle || m > capWin(r));
      if (wraps) {
        // full circle — newest at the apex, age wrapping away both ways.
        const step = TAU / m;
        const start = UP + windowArc / 2 - step * 0.5;
        const seats = centreOut(m, (start + phase - UP) / step, true);
        for (let j = 0; j < m; j++, i++) {
          const it = seq[i];
          // a WHISPER of jitter — enough that the ring isn't a ruler, little
          // enough that it still reads as one arc. ⚠️ ON THE GRID the radial
          // jitter is ZERO and the angular one is halved: both spend the very
          // clearance the band and the pitch were sized to guarantee, and a
          // ±12px wobble across a 101px band is how a label ended up sitting on
          // the folder in the ring below it.
          const jit = (hash01(it.key + "a") - 0.5) * step * (grid ? 0.08 : 0.16);
          const jr = grid ? 0 : (hash01(it.key + "r") - 0.5) * tileBand * 0.18;
          out.push({ it, ring, radius: Math.max(minInner * 0.9, r + jr), baseAngle: start + phase - seats[j] * step + jit, lag, spin: spinOf(ring), i });
        }
      } else {
        // a fan inside the window — centred on the crown, newest at the apex.
        const gap = Math.min(windowArc / Math.max(1, m), (tileArc / r) * 1.15);
        const used = (m - 1) * gap;
        const start = UP + used / 2;
        const seats = centreOut(m, (m - 1) / 2, false);
        for (let j = 0; j < m; j++, i++) {
          const it = seq[i];
          const jit = (hash01(it.key + "a") - 0.5) * gap * 0.16;
          const jr = (hash01(it.key + "r") - 0.5) * tileBand * 0.18;
          out.push({ it, ring, radius: Math.max(minInner * 0.9, r + jr), baseAngle: start - seats[j] * gap + jit, lag, spin: spinOf(ring), i });
        }
      }
    }
    // the bare cells — STRATIFIED, not scattered. The band is divided into
    // (rows × cols) lattice cells and each one holds exactly one blob, jittered
    // inside its own box: even coverage, no clumps, no two on top of each
    // other, and still nothing the eye can read as a grid.
    if (deco > 0) {
      const rows = Math.max(2, Math.round(Math.sqrt(deco / 2)));
      const cols = Math.max(2, Math.ceil(deco / rows));
      const rLo = minInner * 0.66;
      const rHi = outerR * 1.08;
      let d = 0;
      for (let ri = 0; ri < rows && d < deco; ri++) {
        for (let ci = 0; ci < cols && d < deco; ci++, d++) {
          const k = `cell-${ri}-${ci}`;
          const rf = (ri + 0.18 + hash01(k + "r") * 0.64) / rows;
          const af = (ci + 0.18 + hash01(k + "a") * 0.64) / cols;
          const radius = rLo + rf * (rHi - rLo);
          const ring = Math.round((rf) * (ringCount - 1));
          out.push({
            it: { key: k, kind: "cell" },
            ring: Math.max(0, Math.min(ringCount - 1, ring)),
            radius,
            baseAngle: af * TAU,
            lag: TILE_LAG - 0.1 + hash01(k + "l") * 0.2,
            spin: 1,
            i: n + d,
            cs: (isNarrow ? 11 : 14) + hash01(k + "s") * (isNarrow ? 9 : 12),
          });
        }
      }
    }
    return out;
  }, [items, size.w, geom, isNarrow, revolve, deco, ringCounts, fullCircle]);

  // ── the single rAF loop — auto-revolve, netti first, tiles trailing ──────
  const targetSpin = useRef(0);
  const nettiSpin = useRef(0);
  const nettiVel = useRef(0);
  const displaySpin = useRef(0);
  const tileSpin = useRef<Map<string, number>>(new Map());
  const enterT0 = useRef<number | null>(null);
  const lastT = useRef(0);

  useEffect(() => {
    if (!size.w || !tiles.length) return;
    const mirror = origin === "top";
    const paint = (now: number, settleAll: boolean) => {
      const { cx, cy } = geom;
      const e0 = enterT0.current;
      for (const p of tiles) {
        const el = tileEls.current.get(p.it.key);
        if (!el) continue;
        // each ring rides its OWN share of the revolve (p.spin) — on the grid
        // the crown turns a touch faster than the ring inside it, so the field
        // reads as orbits rather than one picture being rotated.
        const want = displaySpin.current * p.spin;
        let ts = tileSpin.current.get(p.it.key);
        if (ts === undefined || settleAll) ts = want;
        else ts += (want - ts) * p.lag;
        tileSpin.current.set(p.it.key, ts);
        const breathe = reduced ? 0 : Math.sin(now * 0.0007 + p.ring * 1.7 + p.radius) * 0.005;
        const a = p.baseAngle + ts + breathe;
        const sinA = Math.sin(a);
        let x = cx + p.radius * Math.cos(a);
        let y = cy - p.radius * sinA;
        // ENTRANCE — each tile flies out of the creature's core to its seat,
        // quick and staggered, then the revolve owns it (the app's fan, in JS
        // so the loop and the entrance can never fight over one style).
        let es = 1;
        if (e0 !== null && !reduced) {
          const e = easeOut((now - e0 - p.i * 45) / 520);
          es = e;
          x = cx + (x - cx) * e;
          y = cy + (y - cy) * e;
        }
        if (mirror) y = size.h - y;
        const below = sinA <= 0.02;
        const aw = ((a % TAU) + TAU) % TAU;
        const edgeProx = Math.min(aw, Math.PI - aw);
        const centerProx = Math.min(1, Math.abs(Math.cos(a)));
        // THE HORIZON FADE ALSO KEEPS TILES OFF THE EDGE. A revolving field
        // wraps the whole circle, so a tile near the horizon sits at cx ± r —
        // outside a phone's width however the radius is bounded. Fading it to
        // NOTHING well before it gets there is what keeps the field inside the
        // screen; on a narrow stage that means holding out until the seat is
        // ~35° off vertical (1.05 rad from the horizon), not 29°.
        // on a phone the field is a CROWN, not a wheel: anything past ~65° off
        // vertical (edgeProx < 0.44 rad from the horizon) is simply gone, so
        // no tile ever reaches the bezel at all — the mask is a backstop, not
        // the mechanism
        // ⚠️ A SPREAD FIELD FADES BY DISTANCE FROM THE BEZEL, not by angle. Its
        // radius is large enough that no angular rule survives contact with a
        // 390px screen: pick a rule loose enough to keep a wide arc and tiles
        // are cut at the edges; pick one tight enough to protect the edges and
        // the field collapses into a vertical column. The thing we actually care
        // about is whether the TILE fits, so measure that — a tile dissolves as
        // its outer edge nears the stage's side, wherever it happens to be. The
        // arc can then be as wide and as tall as it likes.
        // edgeRoom = how much of the tile's box is still on screen (negative
        // once its outer edge has crossed the bezel). It stays WHOLE until its
        // box actually starts to leave, then dissolves over the next 30px —
        // so a tile near the side reads as present, not half-there.
        const halfW = geom.grid ? GRID_TILE_W / 2 : TILE_HALF_W;
        const edgeRoom = size.w / 2 - (Math.abs(x - cx) + halfW);
        const horizFade = revolve
          ? geom.grid
            // THE GRID REACHES THE BEZEL. Its tiles are narrow enough to ride
            // almost to the edge whole, so the dissolve is short and late: full
            // ink while the box has 16px of room, gone by the time it is 12px
            // past. That is what lets the arc span the screen instead of
            // huddling in the middle third.
            ? Math.min(1, Math.max(0, (edgeRoom + 12) / 28))
            : geom.spread
              ? Math.min(1, Math.max(0, (edgeRoom + 30) / 30))
              : isNarrow
                ? Math.min(1, Math.max(0, edgeProx - 0.31) / 0.4)
                : Math.min(1, Math.max(0, edgeProx) / 0.5)
          : 1;
        // THE NEAR EDGE — the side the core is on. A grid ring is wide enough
        // that its far shoulders drop back toward the origin and out of the
        // stage; without this they would be CUT by the box instead of leaving.
        const nearRoom = (mirror ? y : size.h - y) - GRID_TILE_H / 2;
        const nearFade = geom.grid ? Math.min(1, Math.max(0, (nearRoom + 10) / 30)) : 1;
        // a spread field already dissolves at the bezel, so the depth dimming
        // toward the sides is halved — stacked, the two made every tile that was
        // not dead-centre read as a ghost.
        const sideFade = 1 - (geom.spread ? 0.2 : 0.42) * centerProx * centerProx;
        // THE FAR EDGE. Ramping all the way to zero over ~110px meant a folder
        // cresting the top of the arc dimmed to nothing AND was cut by the
        // stage's overflow — it read as broken rather than as depth. Where the
        // crest is a section boundary (`keepCrest`, the create act) the stage no
        // longer clips and the fade only has to suggest distance: a shallower
        // band with a floor that keeps the tile plainly legible. Where the crest
        // IS the top of the window (the hero) the original dissolve stays — a
        // tile held at 62% up there would simply be cut by the browser.
        const fadeD = keepCrest ? Math.max(70, geom.pad) : Math.max(110, geom.pad + 30);
        const farEdge = mirror ? size.h - y : y;
        const rampT = Math.max(0, Math.min(1, farEdge / fadeD));
        const topFade = farEdge < fadeD ? (keepCrest ? 0.62 + 0.38 * rampT : rampT) : 1;
        const visRaw = below ? 0 : horizFade * nearFade * topFade * sideFade * es;
        // a 4%-opacity tile hanging off the edge still reads as a clipped tile —
        // snap the tail to zero rather than leaving a ghost at the bezel
        const vis = visRaw < 0.06 ? 0 : visRaw;
        el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%) scale(${(0.3 + 0.7 * es).toFixed(3)})`;
        el.style.opacity = vis.toFixed(3);
      }
    };

    // seed the field in place (frozen when inactive, alive when active)
    paint(performance.now(), true);
    if (!active) return;
    if (enterT0.current === null) enterT0.current = performance.now() + enterDelay;

    if (reduced) return; // seated, still, honest under reduced motion

    let mounted = true;
    lastT.current = 0;
    const tick = (now: number) => {
      if (!mounted) return;
      const dt = lastT.current ? Math.min(0.05, (now - lastT.current) / 1000) : 1 / 60;
      lastT.current = now;
      // a phone shows a narrower slice of the circle at once, so a spread field
      // turns half again as fast — otherwise waiting for every item to come
      // round takes longer than anyone stays on the section.
      if (revolve) targetSpin.current += rate * (geom.spread ? 1.55 : 1) * dt;
      // netti takes the revolve first (its own spring), the field follows it.
      const want = targetSpin.current * NETTI_FACTOR;
      nettiVel.current += (NETTI_K * (want - nettiSpin.current) - NETTI_C * nettiVel.current) * dt;
      nettiSpin.current += nettiVel.current * dt;
      displaySpin.current += (nettiSpin.current / NETTI_FACTOR - displaySpin.current) * FOLLOW;
      if (driveNetti) {
        window.dispatchEvent(
          new CustomEvent("innernet:organism-drive", { detail: { rotation: mirror ? -nettiSpin.current : nettiSpin.current } }),
        );
      }
      paint(now, false);
      raf = requestAnimationFrame(tick);
    };
    let raf = requestAnimationFrame(tick);
    return () => {
      mounted = false;
      cancelAnimationFrame(raf);
    };
  }, [tiles, size, geom, origin, active, rate, reduced, driveNetti, enterDelay, revolve, keepCrest]);

  // ONE optical VOLUME for every face. An app tile is a square of `appSize`;
  // a folder/page box is landscape (h = w × 0.665), so to fill a similar area
  // its width runs 1.22× the tile's side (√(1/0.665)). Measured, not eyeballed:
  // 56² ≈ 3136 · (68 × 45) ≈ 3060.
  //
  // ⚠️ `dirSize` IS THE RENDERED PIXEL WIDTH here, not DirIcon's `size` prop —
  // the orbit pins `--diricon-scale: 1` (see test1.css), while everywhere else
  // DirIcon shrinks itself by its own responsive law (0.76 desktop / 1.05 phone).
  // So the projects view's `<DirIcon size={74}/>` actually PAINTS 56px wide on a
  // desktop, and passing 74 here made the same folder read 1.32× larger in the
  // orbit than in the pane beside it. `matchProjectsView` does that arithmetic
  // instead of eyeballing a scale: it lands on exactly what the projects grid
  // paints, at either width. The hero keeps the orbit's own volume (0.85 of it).
  const PROJECTS_DIR_SIZE = 74; // what the projects grid passes to DirIcon
  // ⚠️ PHONE_TRIM: on a phone the app's own folder size (74 × 1.05) reads a
  // notch heavier than this section needs — the icons crowded the arc. The
  // landing takes 90% of it, and the projects grid beside it takes the same 90%
  // (see .rg-grid in test1.css) so the two stay identical to each other.
  const PHONE_TRIM = 0.9;
  // ⚠️ THE GRID SIZES ITS OWN ICON. Everything about the grid (band, pitch,
  // seat counts) is measured off GRID_TILE_W/H, and those hold only if the icon
  // inside is the size they were measured for — so the grid pins it rather than
  // inheriting the projects view's footprint. Change GRID_DIR_W and the tile
  // metrics above must be re-measured with it.
  const dirSize = Math.round(
    geom.grid
      ? GRID_DIR_W * iconScale
      : matchProjectsView
        ? PROJECTS_DIR_SIZE * (isNarrow ? 1.05 * PHONE_TRIM : 0.76) * iconScale
        : (isNarrow ? 60 : 74) * iconScale,
  );
  const appSize = Math.round(dirSize / 1.36);
  // one thread per ring, at the ring's own radius (the median of the tiles it
  // seats), over the upper arc the tiles turn through; mirrored with the field
  const ringThreads = useMemo((): Thread[] => {
    if (!withThreads || !tiles.length || !size.w) return [];
    const byRing = new Map<number, number[]>();
    for (const p of tiles) {
      if (p.it.kind === "cell") continue;
      const list = byRing.get(p.ring) ?? [];
      list.push(p.radius);
      byRing.set(p.ring, list);
    }
    const { cx, cy } = geom;
    const flip = origin === "top";
    return [...byRing.entries()].map(([ring, rs]) => {
      const sorted = [...rs].sort((a, b) => a - b);
      const r = sorted[Math.floor(sorted.length / 2)];
      const pts = arcPoints(cx, cy, r, Math.PI * 0.94, Math.PI * 2.06).map(([x, y]) => [x, flip ? size.h - y : y] as [number, number]);
      return { pts, weight: ring === 0 ? 0.5 : 0.6, seed: seedOf(`t1-orbit-ring-${ring}`) };
    });
  }, [withThreads, tiles, size.w, size.h, geom, origin]);
  return (
    <div
      ref={stageRef}
      className={`t1-orbit2${geom.grid ? " t1-orbit2--grid" : ""}${keepCrest ? " t1-orbit2--crest" : ""}${className ? " " + className : ""}`}
      aria-hidden
    >
      {ringThreads.length > 0 && (
        <CellThreads
          className="t1-orbit2__threads"
          threads={ringThreads}
          style={{ animationDelay: `${Math.round(enterDelay + 300)}ms` }}
        />
      )}
      {tiles.map((p) =>
        p.it.kind === "cell" ? (
          <span
            key={p.it.key}
            ref={(el) => {
              if (el) tileEls.current.set(p.it.key, el);
              else tileEls.current.delete(p.it.key);
            }}
            className="t1-orbcell"
            style={{ left: 0, top: 0, opacity: 0, zIndex: 8 + p.ring, "--s": `${(p.cs ?? 16).toFixed(0)}px`, willChange: "transform, opacity" } as React.CSSProperties}
          >
            <span className="t1-orbcell__halo" />
            <span className="t1-orbcell__c" />
          </span>
        ) : (
          <div
            key={p.it.key}
            ref={(el) => {
              if (el) tileEls.current.set(p.it.key, el);
              else tileEls.current.delete(p.it.key);
            }}
            className="orbit-tile t1-orbit2__tile"
            style={{ left: 0, top: 0, opacity: 0, zIndex: 10 + p.ring, willChange: "transform, opacity" }}
          >
            {p.it.kind === "app" ? (
              <AppIcon name={p.it.app ?? p.it.key} size={appSize} />
            ) : (
              <DirIcon icon={p.it.kind} symbol={p.it.symbol ?? "◈"} size={dirSize} />
            )}
            {p.it.name && <span className="orbit-tile-name">{p.it.name}</span>}
            {showMeta && p.it.meta && <span className="orbit-tile-meta">{p.it.meta}</span>}
          </div>
        ),
      )}
    </div>
  );
}
